import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import StatusBadge from "../../../components/RestaurantAdminComponents/StatusBadge";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const moneyToCents = ({ amount, cents }) => {
  const centsNumber = toFiniteNumber(cents);
  if (centsNumber !== null) return Math.round(centsNumber);

  const amountNumber = toFiniteNumber(amount);
  if (amountNumber !== null) return Math.round(amountNumber * 100);

  return 0;
};

const formatUsdFromCents = (cents) => USD_FORMATTER.format(cents / 100);

const formatReadableLabel = (value) => (
  (value || "Unknown")
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const getTransactionRows = (responseData) => {
  const payload = responseData?.data || responseData || {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.transactions)) return payload.transactions;
  if (Array.isArray(responseData?.transactions)) return responseData.transactions;
  return [];
};

const getTransactionDate = (tx) => tx.transaction_date || tx.completed_at || tx.created_at;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatSignedTransactionAmount = (row) => {
  const amountCents = Math.abs(moneyToCents({ amount: row.amount, cents: row.amount_cents }));
  const type = (row.type || "").toString().toLowerCase();
  const prefix = type === "credit" ? "+" : type === "debit" ? "-" : "";
  return `${prefix}${formatUsdFromCents(amountCents)}`;
};

/* -------------------------------------------------------------------------
 * RestaurantTransactionHistoryPage — full transaction history table view.
 * Similar pattern to RestaurantPastSettlementsPage, showing all transactions.
 * ---------------------------------------------------------------------- */

const columns = [
  {
    key: "transaction_date",
    label: "Date",
    width: "1.1fr",
    render: (r) => formatDate(getTransactionDate(r)),
  },
  {
    key: "description",
    label: "Description",
    width: "1.5fr",
    render: (r) => r.description || 'Wallet transaction',
  },
  {
    key: "type",
    label: "Type",
    width: "0.7fr",
    render: (r) => <StatusBadge label={formatReadableLabel(r.type)} />,
  },
  {
    key: "amount",
    label: "Amount",
    width: "0.8fr",
    align: "right",
    render: (r) => formatSignedTransactionAmount(r),
  },
  {
    key: "status",
    label: "Status",
    width: "0.8fr",
    render: (r) => <StatusBadge label={formatReadableLabel(r.status)} />,
  },
];

const RestaurantTransactionHistoryPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Transaction History | Omaha Food Lovers";
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("page_size", 20);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const fetchTransactions = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.restaurantStripeConnectTransactions}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const newTransactions = getTransactionRows(response.data);

        if (isLoadMore) {
          setTransactions((prev) => [...prev, ...newTransactions]);
        } else {
          setTransactions(newTransactions);
        }

        // Simple pagination - if we got fewer results than page_size, no more data
        setHasMore(newTransactions.length === 20);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchTransactions(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "created_at",
      options: [
        { value: "created_at", label: "Date" },
        { value: "amount", label: "Amount" },
        { value: "type", label: "Type" },
        { value: "status", label: "Status" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchTransactions(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Transaction History</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of wallet transactions and payment history.</p>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search transactions"
            filters={filters}
          />

        <DataTable
          columns={columns}
          rows={transactions}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
        </div>
      </Container>
    </div>
  );
};

RestaurantTransactionHistoryPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

RestaurantTransactionHistoryPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default RestaurantTransactionHistoryPage;
